import { z } from 'zod';

export const accountScopeProofSchema = z.object({
  mode: z.literal('selected'),
  account_ids: z.array(z.string()),
}).strict();

export type AccountScopeProof = z.infer<typeof accountScopeProofSchema>;
export type AccountScopeMode = 'all' | 'selected';

export type AccountScope = Readonly<{
  accountIds: readonly string[];
  mode: AccountScopeMode;
  fingerprint: string;
}>;

export const MULTI_ACCOUNT_SCOPE_UNAVAILABLE_REASON = 'multi_account_scope_unavailable' as const;
export type AccountScopeUnavailableReason = typeof MULTI_ACCOUNT_SCOPE_UNAVAILABLE_REASON;

export class AccountScopeUnavailableError extends Error {
  readonly reason = MULTI_ACCOUNT_SCOPE_UNAVAILABLE_REASON;

  constructor() {
    super('The transactions response did not prove the requested account scope.');
    this.name = 'AccountScopeUnavailableError';
  }
}

export function freezeAccountIds(accountIds: readonly string[]): readonly string[] {
  return Object.freeze([...accountIds]);
}

export function accountScopeFingerprint(accountIds: readonly string[]): string {
  return accountIds.length === 0 ? 'all' : `selected:${JSON.stringify(accountIds)}`;
}

export function createAccountScope(accountIds: readonly string[]): AccountScope {
  const frozenAccountIds = freezeAccountIds(accountIds);
  const mode: AccountScopeMode = frozenAccountIds.length === 0 ? 'all' : 'selected';
  return Object.freeze({
    accountIds: frozenAccountIds,
    mode,
    fingerprint: accountScopeFingerprint(frozenAccountIds),
  });
}

export function hasExactAccountScopeProof(
  meta: { account_scope?: AccountScopeProof },
  accountIds: readonly string[],
): boolean {
  return accountIds.length <= 1 || (
    meta.account_scope?.mode === 'selected' &&
    meta.account_scope.account_ids.length === accountIds.length &&
    meta.account_scope.account_ids.every((id, index) => id === accountIds[index])
  );
}

export function assertAccountScopeProof(
  meta: { account_scope?: AccountScopeProof },
  accountIds: readonly string[],
): void {
  if (!hasExactAccountScopeProof(meta, accountIds)) throw new AccountScopeUnavailableError();
}
